import React from 'react';
import {
    Drawer,
    Typography,
    Row,
    Col,
    Space,
    Image,
    Tag,
    Divider
} from 'antd';
import {
    ShopOutlined,
    MailOutlined,
    PhoneOutlined,
    GlobalOutlined,
    FileTextOutlined,
    UserOutlined,
    LinkOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import env from '../../env';
import LeafletMap from './LeafletMap';

const { Title, Text } = Typography;

const CompanyDrawer = ({ visible, onClose, companyData, userId = null }) => {
    if (!companyData) {
        return null;
    }

    return (
        <Drawer
            title={
                <Space>
                    <ShopOutlined />
                    <span>Company Profile</span>
                </Space>
            }
            placement="right"
            onClose={onClose}
            open={visible}
            width={600}
            bodyStyle={{ padding: '24px' }}
        >
            <div style={{ 
                background: '#fff',
                fontFamily: 'Arial, sans-serif'
            }}>
                {/* Header Section */}
                <div style={{ 
                    borderBottom: '2px solid #e8e8e8',
                    paddingBottom: '16px',
                    marginBottom: '20px'
                }}>
                    <Row gutter={16} align="middle">
                        <Col span={6}>
                            {companyData.company_logo_url ? (
                                <Image
                                    src={companyData.company_logo_url.startsWith('http') 
                                        ? companyData.company_logo_url 
                                        : env.BACKEND_URL + companyData.company_logo_url}
                                    alt="Company Logo"
                                    width={80}
                                    style={{ borderRadius: 6 }}
                                    preview={false}
                                />
                            ) : (
                                <div style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 6,
                                    background: 'linear-gradient(135deg, #1890ff, #52c41a)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '32px'
                                }}>
                                    {companyData.company_name?.charAt(0).toUpperCase() || 'C'}
                                </div>
                            )}
                        </Col>
                        <Col span={18}>
                            <Title level={4} style={{ 
                                margin: 0,
                                color: '#262626',
                                fontWeight: '600',
                                fontSize: '18px'
                            }}>
                                {companyData.company_name || 'Company Name Not Provided'}
                            </Title>
                            {companyData.short_tagline && (
                                <Text style={{ 
                                    fontSize: '12px',
                                    color: '#8c8c8c',
                                    fontStyle: 'italic',
                                    marginTop: '4px',
                                    display: 'block'
                                }}>
                                    {companyData.short_tagline}
                                </Text>
                            )}
                        </Col>
                    </Row>
                </div>

                {/* Banner Image */}
                {companyData.banner_image_url && (
                    <div style={{ marginBottom: '20px' }}>
                        <Image
                            src={companyData.banner_image_url.startsWith('http') 
                                ? companyData.banner_image_url 
                                : env.BACKEND_URL + companyData.banner_image_url}
                            alt="Banner Image"
                            width="100%"
                            style={{ 
                                maxHeight: 150, 
                                objectFit: 'cover', 
                                borderRadius: 6,
                                display: 'block'
                            }}
                            preview={false}
                        />
                    </div>
                )}

                {/* Company Information Section */}
                <div style={{ marginBottom: '20px' }}>
                    <Title level={5} style={{ 
                        marginBottom: '10px',
                        color: '#262626',
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '6px',
                        fontWeight: '600',
                        fontSize: '14px'
                    }}>
                        <ShopOutlined style={{ marginRight: '6px' }} />
                        Company Information
                    </Title>
                    <div style={{ 
                        background: '#fafafa',
                        padding: '12px',
                        borderRadius: '4px',
                        border: '1px solid #f0f0f0'
                    }}>
                        <Row gutter={[12, 8]}>
                            {companyData.business_type && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Business Type
                                        </Text>
                                        <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                            {companyData.business_type}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                            {companyData.company_registration_number && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Registration Number
                                        </Text>
                                        <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                            {companyData.company_registration_number}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                            {companyData.tax_ntn_number && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Tax/NTN Number
                                        </Text>
                                        <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                            {companyData.tax_ntn_number}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                            {companyData.business_address && (
                                <Col span={24}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Business Address
                                        </Text>
                                        <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                            {companyData.business_address}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                </div>

                {/* Map Section - Show business address on map */}
                {companyData.business_address && (
                    <div style={{ marginBottom: '20px' }}>
                        <Title level={5} style={{ 
                            marginBottom: '10px',
                            color: '#262626',
                            borderBottom: '1px solid #f0f0f0',
                            paddingBottom: '6px',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            <EnvironmentOutlined style={{ marginRight: '6px' }} />
                            Location
                        </Title>
                        <div style={{ 
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0',
                            overflow: 'hidden'
                        }}>
                            {visible ? (
                                <LeafletMap
                                    location={companyData.business_address}
                                    height="300px"
                                    zoom={13}
                                    showOpenInMaps={true}
                                    style={{ marginBottom: '12px' }}
                                />
                            ) : (
                                <div style={{
                                    width: '100%',
                                    height: '300px',
                                    borderRadius: '4px',
                                    border: '1px solid #e8e8e8',
                                    background: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '12px',
                                    flexDirection: 'column',
                                    gap: '8px'
                                }}>
                                    <EnvironmentOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                                    <Text type="secondary" style={{ fontSize: '14px' }}>
                                        Map will load when drawer opens
                                    </Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Contact Information Section */}
                <div style={{ marginBottom: '20px' }}>
                    <Title level={5} style={{ 
                        marginBottom: '10px',
                        color: '#262626',
                        borderBottom: '1px solid #f0f0f0',
                        paddingBottom: '6px',
                        fontWeight: '600',
                        fontSize: '14px'
                    }}>
                        <MailOutlined style={{ marginRight: '6px' }} />
                        Contact Information
                    </Title>
                    <div style={{ 
                        background: '#fafafa',
                        padding: '12px',
                        borderRadius: '4px',
                        border: '1px solid #f0f0f0'
                    }}>
                        <Row gutter={[12, 8]}>
                            {companyData.email_address && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Email Address
                                        </Text>
                                        <a href={`mailto:${companyData.email_address}`} style={{ 
                                            fontSize: '13px',
                                            color: '#1890ff'
                                        }}>
                                            <MailOutlined style={{ marginRight: '4px', fontSize: '12px' }} />
                                            {companyData.email_address}
                                        </a>
                                    </div>
                                </Col>
                            )}
                            {companyData.phone_whatsapp && (
                                <Col span={12}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Phone / WhatsApp
                                        </Text>
                                        <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                            <PhoneOutlined style={{ marginRight: '4px', fontSize: '12px' }} />
                                            {companyData.phone_whatsapp}
                                        </Text>
                                    </div>
                                </Col>
                            )}
                            {companyData.website && (
                                <Col span={24}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ 
                                            fontSize: '11px',
                                            display: 'block',
                                            marginBottom: '2px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.3px'
                                        }}>
                                            Website
                                        </Text>
                                        <a href={companyData.website} target="_blank" rel="noopener noreferrer" style={{ 
                                            fontSize: '13px',
                                            color: '#1890ff'
                                        }}>
                                            <GlobalOutlined style={{ marginRight: '4px', fontSize: '12px' }} />
                                            {companyData.website}
                                        </a>
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                </div>

                {/* Company Description Section */}
                {companyData.company_description && (
                    <div style={{ marginBottom: '20px' }}>
                        <Title level={5} style={{ 
                            marginBottom: '10px',
                            color: '#262626',
                            borderBottom: '1px solid #f0f0f0',
                            paddingBottom: '6px',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            <FileTextOutlined style={{ marginRight: '6px' }} />
                            Company Description
                        </Title>
                        <div style={{ 
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0'
                        }}>
                            <Text style={{ 
                                fontSize: '13px',
                                color: '#262626',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {companyData.company_description}
                            </Text>
                        </div>
                    </div>
                )}

                {/* Social Media Links Section */}
                {companyData.social_media_links && 
                 Array.isArray(companyData.social_media_links) && 
                 companyData.social_media_links.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                        <Title level={5} style={{ 
                            marginBottom: '10px',
                            color: '#262626',
                            borderBottom: '1px solid #f0f0f0',
                            paddingBottom: '6px',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            <LinkOutlined style={{ marginRight: '6px' }} />
                            Social Media Links
                        </Title>
                        <div style={{ 
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0'
                        }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                {companyData.social_media_links.map((link, index) => (
                                    <a 
                                        key={index} 
                                        href={link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        style={{ 
                                            display: 'block',
                                            fontSize: '13px',
                                            color: '#1890ff',
                                            padding: '4px 0',
                                            borderBottom: index < companyData.social_media_links.length - 1 ? '1px solid #f0f0f0' : 'none'
                                        }}
                                    >
                                        <LinkOutlined style={{ marginRight: '6px', fontSize: '12px' }} />
                                        {link}
                                    </a>
                                ))}
                            </Space>
                        </div>
                    </div>
                )}

                {/* Owner Information Section - Only show if owner info is provided */}
                {(companyData.user_name || companyData.user_email) && (
                    <div style={{ marginBottom: '20px' }}>
                        <Title level={5} style={{ 
                            marginBottom: '10px',
                            color: '#262626',
                            borderBottom: '1px solid #f0f0f0',
                            paddingBottom: '6px',
                            fontWeight: '600',
                            fontSize: '14px'
                        }}>
                            <UserOutlined style={{ marginRight: '6px' }} />
                            Owner Information
                        </Title>
                        <div style={{ 
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '4px',
                            border: '1px solid #f0f0f0'
                        }}>
                            <Row gutter={[12, 8]}>
                                {companyData.user_name && (
                                    <Col span={12}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Text type="secondary" style={{ 
                                                fontSize: '11px',
                                                display: 'block',
                                                marginBottom: '2px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                            }}>
                                                Owner Name
                                            </Text>
                                            <Text strong style={{ fontSize: '13px', color: '#262626' }}>
                                                {companyData.user_name}
                                            </Text>
                                        </div>
                                    </Col>
                                )}
                                {companyData.user_email && (
                                    <Col span={12}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Text type="secondary" style={{ 
                                                fontSize: '11px',
                                                display: 'block',
                                                marginBottom: '2px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.3px'
                                            }}>
                                                Owner Email
                                            </Text>
                                            <a href={`mailto:${companyData.user_email}`} style={{ 
                                                fontSize: '13px',
                                                color: '#1890ff'
                                            }}>
                                                <MailOutlined style={{ marginRight: '4px', fontSize: '12px' }} />
                                                {companyData.user_email}
                                            </a>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </div>
                )}

                {/* Footer Section - Only show if created_at/updated_at are provided */}
                {(companyData.created_at || companyData.updated_at) && (
                    <div style={{ 
                        marginTop: '20px',
                        paddingTop: '12px',
                        borderTop: '2px solid #e8e8e8',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div>
                            {companyData.created_at && (
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                                    Created: {new Date(companyData.created_at).toLocaleString()}
                                </Text>
                            )}
                            {companyData.updated_at && (
                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '2px' }}>
                                    Last Updated: {new Date(companyData.updated_at).toLocaleString()}
                                </Text>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default CompanyDrawer;

